import { useState, FormEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  privacy: boolean;
};

const initialFormState: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
  privacy: false,
};

export default function ContactSection() {
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setError('必須項目を入力してください。');
      return;
    }

    if (!formData.privacy) {
      setError('プライバシーポリシーへの同意が必要です。');
      return;
    }

    if (formData.message.length > 500) {
      setError('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setError('');
    setIsConfirmOpen(true);
  };

  const submitForm = async () => {
    setIsConfirmOpen(false);
    setIsSubmitting(true);
    setSubmitState('idle');
    setError('');

    const body = new URLSearchParams({
      name: formData.name,
      company: formData.company,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      privacy: formData.privacy ? '同意済み' : '',
    });

    try {
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      const text = await response.text();
      if (!response.ok || text.trim() !== 'OK') {
        throw new Error('failed');
      }

      setSubmitState('success');
      setFormData(initialFormState);
    } catch {
      setSubmitState('error');
      setError('送信に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-400/5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400/5 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 border-2 border-yellow-400 rounded-full px-6 py-2 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-yellow-500 text-sm"></i>
            </div>
            <span className="text-yellow-600 text-xs font-bold tracking-widest uppercase">Contact</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', 'Noto Sans JP', sans-serif" }}>
            お問い合わせ
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            お気軽にご相談ください。担当者より2営業日以内にご連絡いたします。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="rounded-2xl bg-gray-50 p-7 border border-gray-100">
              <h3 className="font-bold text-gray-900 text-lg mb-5">会社情報</h3>
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-yellow-400/20 mt-0.5 shrink-0">
                    <i className="ri-building-line text-yellow-600 text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">会社名</div>
                    <div className="text-sm font-semibold text-gray-800">合同会社アテディア</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-yellow-400/20 mt-0.5 shrink-0">
                    <i className="ri-map-pin-line text-yellow-600 text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">所在地</div>
                    <div className="text-sm font-semibold text-gray-800">大阪府大阪市東成区深江北一丁目6-31-601</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-yellow-400/20 mt-0.5 shrink-0">
                    <i className="ri-calendar-line text-yellow-600 text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">設立</div>
                    <div className="text-sm font-semibold text-gray-800">令和4年9月5日</div>
                  </div>
                </div>

              </div>
            </div>

            {/* Gear decoration */}
            <div className="hidden lg:flex items-center justify-center py-4 opacity-30">
              <i className="ri-settings-3-line text-yellow-400 animate-spin" style={{ fontSize: '80px', animationDuration: '8s' }}></i>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-3">
            <form
              id="contact-form"
              onSubmit={handleSubmit}
              className="rounded-2xl bg-white border border-gray-100 p-7 md:p-10"
              style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">会社名</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="株式会社〇〇"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">電話番号</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06-0000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="ご相談内容をご記入ください..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all resize-none"
                  />
                  <div className={`text-right text-xs mt-1 ${formData.message.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                    {formData.message.length}/500文字
                  </div>
                </div>

                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleChange}
                      className="mt-1 h-5 w-5 cursor-pointer rounded border border-gray-300 accent-yellow-400"
                    />
                    <span className="text-sm text-gray-600 leading-relaxed">
                      <Link
                        to="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-600 underline hover:text-yellow-500 font-semibold"
                      >
                        プライバシーポリシー
                      </Link>
                      に同意する
                    </span>
                  </label>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-black text-white font-bold py-4 rounded-full hover:bg-yellow-400 hover:text-black transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-settings-3-line animate-spin"></i>
                      </div>
                      送信中...
                    </>
                  ) : (
                    <>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-send-plane-line"></i>
                      </div>
                      送信する
                    </>
                  )}
                </button>
              </form>
          </div>
        </div>
      </div>

      {isConfirmOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">入力内容の確認</h3>
            <p className="text-sm text-gray-500 mb-6">以下の内容で送信します。</p>

            <div className="space-y-4 mb-8">
              {[
                { label: 'お名前', value: formData.name },
                { label: '会社名', value: formData.company || '未入力' },
                { label: 'メールアドレス', value: formData.email },
                { label: '電話番号', value: formData.phone || '未入力' },
                { label: 'プライバシーポリシー同意', value: formData.privacy ? '同意済み' : '未同意' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-gray-100 px-5 py-4">
                  <p className="text-xs font-bold text-gray-500 mb-1">{item.label}</p>
                  <p className="text-sm text-gray-800">{item.value}</p>
                </div>
              ))}

              <div className="rounded-2xl border border-gray-100 px-5 py-4">
                <p className="text-xs font-bold text-gray-500 mb-1">お問い合わせ内容</p>
                <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{formData.message}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setIsConfirmOpen(false)}
                className="flex-1 rounded-full bg-gray-100 py-3.5 font-bold text-gray-700 transition hover:bg-gray-200"
              >
                修正する
              </button>
              <button
                type="button"
                onClick={submitForm}
                className="flex-1 rounded-full bg-yellow-400 py-3.5 font-bold text-black transition hover:bg-yellow-300"
              >
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 text-yellow-600">
              <i className="ri-loader-4-line animate-spin text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">送信中です</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              お問い合わせ内容を送信しています。しばらくお待ちください。
            </p>
          </div>
        </div>
      )}

      {submitState !== 'idle' && !isSubmitting && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
            <div
              className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${
                submitState === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}
            >
              <i className={`${submitState === 'success' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'} text-3xl`}></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {submitState === 'success' ? '送信完了' : '送信失敗'}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              {submitState === 'success'
                ? 'お問い合わせを受け付けました。担当者より折り返しご連絡いたします。'
                : '送信に失敗しました。時間を置いて再度お試しください。'}
            </p>
            <button
              type="button"
              onClick={() => setSubmitState('idle')}
              className="w-full rounded-full bg-black py-3.5 font-bold text-white transition hover:bg-yellow-400 hover:text-black"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
